import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  appendLiveLessonEvent,
  commitLiveLessonReview,
  discardLiveLessonReview,
  fetchLiveLessonSession,
  saveLiveLessonSession,
  updateLiveLessonBoardState
} from '../../lib/liveLessonApi';
import type {
  BoardDisplayState,
  LiveLessonDisplayOverlays,
  ClassroomEventType,
  LiveLessonSession,
  StudentObservation,
  TemporaryNeedHelpState
} from '../../lib/liveLessonTypes';

type LiveSessionContextValue = {
  session: LiveLessonSession | null;
  observations: StudentObservation[];
  helpStates: TemporaryNeedHelpState[];
  loading: boolean;
  setSession: (next: LiveLessonSession) => void;
  setObservations: (next: StudentObservation[]) => void;
  setHelpStates: (next: TemporaryNeedHelpState[]) => void;
  saveAll: (nextSession: LiveLessonSession, nextObservations: StudentObservation[], nextHelpStates: TemporaryNeedHelpState[]) => Promise<void>;
  emitEvent: (type: ClassroomEventType, payload: Record<string, unknown>) => Promise<void>;
  syncBoardState: (
    pin: string,
    boardDisplayState: BoardDisplayState,
    liveLessonOverlays?: LiveLessonDisplayOverlays
  ) => Promise<void>;
  commitReview: (ids: string[]) => Promise<void>;
  discardReview: (ids: string[]) => Promise<void>;
};

const LiveSessionContext = createContext<LiveSessionContextValue | null>(null);

type LiveSessionProviderProps = {
  teachingUnitId: number;
  children: React.ReactNode;
};

function createDefaultSession(teachingUnitId: number): LiveLessonSession {
  return {
    id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`,
    teachingUnitId,
    timetableEntryId: null,
    status: 'preparing',
    startedAt: null,
    endedAt: null,
    classContext: { parentType: 'ad_hoc', label: 'Ad hoc' },
    sitzplanId: null,
    activeCompetencyIds: [],
    currentPhaseId: null,
    activeScene: 'focus',
    workMode: 'individual',
    displayPin: null,
    teacherStateVersion: 1,
    boardStateVersion: 1
  };
}

export function LiveSessionProvider({ teachingUnitId, children }: LiveSessionProviderProps) {
  const [session, setSession] = useState<LiveLessonSession | null>(null);
  const [observations, setObservations] = useState<StudentObservation[]>([]);
  const [helpStates, setHelpStates] = useState<TemporaryNeedHelpState[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchLiveLessonSession(teachingUnitId);
        if (!mounted) return;
        setSession(data.session || createDefaultSession(teachingUnitId));
        setObservations(data.observations || []);
        setHelpStates(data.helpStates || []);
      } catch (error: any) {
        if (!mounted) return;
        setSession(createDefaultSession(teachingUnitId));
        toast.error(error?.response?.data?.error || 'Live-Session konnte nicht geladen werden');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [teachingUnitId]);

  const saveAll = useCallback(
    async (nextSession: LiveLessonSession, nextObservations: StudentObservation[], nextHelpStates: TemporaryNeedHelpState[]) => {
      await saveLiveLessonSession(teachingUnitId, {
        session: nextSession,
        observations: nextObservations,
        helpStates: nextHelpStates
      });
      setSession(nextSession);
      setObservations(nextObservations);
      setHelpStates(nextHelpStates);
    },
    [teachingUnitId]
  );

  const emitEvent = useCallback(
    async (type: ClassroomEventType, payload: Record<string, unknown>) => {
      if (!session) return;
      await appendLiveLessonEvent(teachingUnitId, {
        event: {
          id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`,
          sessionId: session.id,
          type,
          at: new Date().toISOString(),
          payload
        }
      });
    },
    [session, teachingUnitId]
  );

  const syncBoardState = useCallback(
    async (pin: string, boardDisplayState: BoardDisplayState, liveLessonOverlays?: LiveLessonDisplayOverlays) => {
      await updateLiveLessonBoardState(teachingUnitId, { pin, boardDisplayState, liveLessonOverlays });
    },
    [teachingUnitId]
  );

  const commitReview = useCallback(
    async (ids: string[]) => {
      const result = await commitLiveLessonReview(teachingUnitId, { observationIds: ids });
      if (result.committedIds.length) {
        setObservations((prev) =>
          prev.map((entry) =>
            result.committedIds.includes(entry.id) ? { ...entry, status: 'committed' } : entry
          )
        );
      }
    },
    [teachingUnitId]
  );

  const discardReview = useCallback(
    async (ids: string[]) => {
      const result = await discardLiveLessonReview(teachingUnitId, { observationIds: ids });
      if (result.discardedIds.length) {
        setObservations((prev) =>
          prev.map((entry) =>
            result.discardedIds.includes(entry.id) ? { ...entry, status: 'discarded' } : entry
          )
        );
      }
    },
    [teachingUnitId]
  );

  const value = useMemo(
    () => ({
      session,
      observations,
      helpStates,
      loading,
      setSession,
      setObservations,
      setHelpStates,
      saveAll,
      emitEvent,
      syncBoardState,
      commitReview,
      discardReview
    }),
    [session, observations, helpStates, loading, saveAll, emitEvent, syncBoardState, commitReview, discardReview]
  );

  return <LiveSessionContext.Provider value={value}>{children}</LiveSessionContext.Provider>;
}

export function useLiveSession() {
  const context = useContext(LiveSessionContext);
  if (!context) {
    throw new Error('useLiveSession must be used inside LiveSessionProvider');
  }
  return context;
}
